import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import {newEntryProblem} from '../../utils/treeEntryNames';
import './TreeDialogs.css';

/**
 * "New file" / "New folder" of the Code tree: the name, where it goes (`location`, a sentence such as "in
 * services/app/src"), then Create. The name is checked as it is typed (newEntryProblem: not empty, no "\", not "." or
 * "..", not already there; a "/" only with `allowNested`, which creates the missing folders too) and Create stays off
 * until it is fine. Enter creates, Escape cancels. `onCreate(name)` returns a promise; while it runs Create spins and
 * the dialog cannot be closed; a failure is shown in the dialog.
 */
const TreeCreateDialog = ({kind, location, allowNested = false, entryAt, onCreate, onClose}) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const problem = newEntryProblem(name, {allowNested, entryAt});
  const canCreate = !problem && !busy;
  const nested = !problem && name.trim().includes('/');
  const noun = kind === 'folder' ? 'folder' : 'file';
  const fieldId = `tree-create-${noun}-name`;

  const create = async () => {
    setTouched(true);
    if (!canCreate) { return; }
    setBusy(true);
    setError(null);
    try {
      await onCreate(name.trim());
    } catch (failed) {
      setError(failed?.message || `The ${noun} could not be created.`);
      setBusy(false);
    }
  };
  const cancel = () => { if (!busy) { onClose(); } };

  return (
    <Modal className="tree-dialog-modal" isOpen onClose={busy ? null : onClose} title={kind === 'folder' ? 'New folder' : 'New file'} type="custom">
      <div
        className="tree-dialog"
        data-testid="tree-create-dialog"
        onKeyDown={(event) => {
          if (event.key === 'Escape') { event.preventDefault(); cancel(); }
        }}
      >
        <label className="tree-dialog__label" htmlFor={fieldId}>{kind === 'folder' ? 'Folder name' : 'File name'}</label>
        <input
          aria-describedby={`${fieldId}-where`}
          aria-invalid={Boolean(touched && problem && name) || undefined}
          autoComplete="off"
          autoFocus
          className="tree-dialog__input"
          data-testid="tree-create-name"
          disabled={busy}
          id={fieldId}
          onChange={(event) => { setName(event.target.value); setTouched(true); setError(null); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); create(); }
          }}
          spellCheck={false}
          type="text"
          value={name}
        />
        <p className="tree-dialog__where" data-testid="tree-create-location" id={`${fieldId}-where`}>
          {location}
          {nested && <span className="tree-dialog__hint">{' · the missing folders are created too'}</span>}
        </p>
        {/* The reason once something was typed (an empty field just keeps Create off). */}
        {touched && problem && name && <p className="tree-dialog__error" data-testid="tree-create-problem" role="alert">{problem}</p>}
        {error && <p className="tree-dialog__error" role="alert">{error}</p>}
        <div className="tree-dialog__actions">
          <button className="tree-dialog__btn" disabled={busy} onClick={cancel} type="button">Cancel</button>
          <button
            aria-busy={busy || undefined}
            className="tree-dialog__btn tree-dialog__btn--primary"
            data-testid="tree-create-confirm"
            disabled={!canCreate}
            onClick={create}
            type="button"
          >
            {busy && <span aria-hidden className="tree-dialog__spinner" />}
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

TreeCreateDialog.propTypes = {
  allowNested: PropTypes.bool,
  entryAt: PropTypes.func.isRequired,
  kind: PropTypes.oneOf(['file', 'folder']).isRequired,
  location: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default TreeCreateDialog;
